import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import useTranslation from '../../customHooks/useTranslation';
import useLanguage from '../../customHooks/useLanguage';
import { formatSlot, useBooking } from '../../contextApi/BookingContext';
import { BOOKING_URL, BRIEF_ENDPOINT, EMAIL, GITHUB_URL, LINKEDIN_URL, PHONE, PHONE_HREF } from '../../config/site';
import { Sheet, SheetHeading } from './Sheet';

type Status = 'idle' | 'sending' | 'success' | 'error';
type Field = 'name' | 'email' | 'phone' | 'message';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const PHONE_RE = /^\+?[\d\s().-]{7,20}$/;

const SHAKE = [{ transform: 'translateX(0)' }, { transform: 'translateX(-7px)' }, { transform: 'translateX(6px)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(2px)' }, { transform: 'translateX(0)' }];

// ITU country codes that are two digits long; +1 and +7 are one, the rest three.
const CC_TWO = /^(2[07]|3[0-469]|4[013-9]|5[1-8]|6[0-6]|8[1246]|9[0-58])/;

// Groups a number into threes, the way it is read aloud: "123 456 789", "+48 123 456 789".
// A lone trailing digit joins the group before it ("123 456 7890") instead of dangling.
export function formatPhone(raw: string) {
	const plus = raw.trimStart().startsWith('+');
	let digits = raw.replace(/\D/g, '').slice(0, 15);
	let cc = '';
	if (plus) {
		const ccLength = /^[17]/.test(digits) ? 1 : CC_TWO.test(digits) ? 2 : 3;
		cc = digits.slice(0, ccLength);
		digits = digits.slice(ccLength);
	}
	const groups = digits.match(/\d{1,3}/g) ?? [];
	if (groups.length > 1 && groups[groups.length - 1].length === 1) groups.splice(-2, 2, groups[groups.length - 2] + groups[groups.length - 1]);
	return plus ? ['+' + cc, ...groups].join(' ') : groups.join(' ');
}

// Reformats a phone input as the visitor types, keeping the caret after the same digit it followed.
export function formatPhoneInput(el: HTMLInputElement) {
	const significant = (str: string) => str.replace(/[^\d+]/g, '').length;
	const before = significant(el.value.slice(0, el.selectionStart ?? el.value.length));
	const formatted = formatPhone(el.value);
	let caret = 0;
	for (let seen = 0; caret < formatted.length && seen < before; caret++) if (/[\d+]/.test(formatted[caret])) seen++;
	requestAnimationFrame(() => el.setSelectionRange(caret, caret));
	return formatted;
}

export default function Contact() {
	const t = useTranslation();
	const { language } = useLanguage();
	const b = t.contact.brief;
	const { slot, topic, setTopic } = useBooking();
	const locale = language === 'pl' ? 'pl-PL' : 'en-GB';
	const slotLabel = formatSlot(slot, locale);

	const [values, setValues] = useState({ name: '', email: '', phone: '', message: '' });
	const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});
	const [status, setStatus] = useState<Status>('idle');
	const [attempted, setAttempted] = useState(false);
	const submitRef = useRef<HTMLButtonElement>(null);
	const successRef = useRef<HTMLParagraphElement>(null);

	// Keyboard and screen-reader users land on the outcome instead of the page body.
	useEffect(() => {
		if (status === 'success') successRef.current?.focus();
		if (status === 'error') submitRef.current?.focus();
	}, [status]);

	const errors: Partial<Record<Field, string>> = {};
	if (!values.name.trim()) errors.name = b.errors.name;
	if (!EMAIL_RE.test(values.email.trim())) errors.email = b.errors.email;
	if (values.phone.trim() && !PHONE_RE.test(values.phone.trim())) errors.phone = b.errors.phone;
	if (values.message.trim().length < 5) errors.message = b.errors.message;
	const show = (f: Field) => (touched[f] ? errors[f] : undefined);

	useEffect(() => {
		if (status === 'error') setStatus('idle');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values]);

	const update = (f: Field) => (e: { target: { value: string } }) => setValues(v => ({ ...v, [f]: e.target.value }));
	const updatePhone = (e: ChangeEvent<HTMLInputElement>) => setValues(v => ({ ...v, phone: formatPhoneInput(e.target) }));
	const blur = (f: Field) => () => setTouched(tt => ({ ...tt, [f]: true }));

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		setTouched({ name: true, email: true, phone: true, message: true });
		if (Object.keys(errors).length > 0) {
			setAttempted(true);
			const invalid = (['name', 'email', 'phone', 'message'] as Field[]).filter(f => errors[f]);
			const focusFirst = () => {
				const field = document.getElementById(`brief-${invalid[0]}`);
				// Bring the whole field, label included, clear of the sticky header before focusing it.
				field?.parentElement?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
				field?.focus({ preventScroll: true });
			};
			if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return focusFirst();
			// The button and every field still to fix refuse together with a short shake,
			// then focus moves to the first of those fields.
			const targets = [submitRef.current, ...invalid.map(f => document.getElementById(`brief-${f}`)?.parentElement)].filter((el): el is HTMLElement => !!el);
			Promise.all(targets.map(el => el.animate(SHAKE, { duration: 380, easing: 'cubic-bezier(0.36, 0.07, 0.19, 0.97)' }).finished)).then(focusFirst, focusFirst);
			return;
		}
		setStatus('sending');
		try {
			const response = await fetch(BRIEF_ENDPOINT, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					source: 'main',
					name: values.name.trim(),
					email: values.email.trim(),
					phone: values.phone ? values.phone.trim() : '',
					topic: b.topics[topic],
					preferredSlot: slotLabel || null,
					message: values.message.trim()
				}),
			});
			setStatus(response.ok ? 'success' : 'error');
		} catch {
			setStatus('error');
		}
	};

	const reset = () => {
		setValues({ name: '', email: '', phone: '', message: '' });
		setTouched({});
		setAttempted(false);
		setStatus('idle');
		requestAnimationFrame(() => document.getElementById('brief-name')?.focus());
	};

	return (
		<Sheet id='contact' code='A-501' title={t.nav.sheets.contact} className='drafting-grid'>
			<div className='mx-auto grid max-w-[1320px] gap-x-12 gap-y-12 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:py-28'>
				<div className='lg:col-span-4'>
					<SheetHeading id='contact'>{t.contact.heading}</SheetHeading>
					<p className='mt-5 max-w-[40ch] text-lg leading-relaxed text-ink-soft'>{t.contact.intro}</p>

					<div className='mt-10 border-t border-ink-line pt-6'>
						<h3 className='type-wide text-xl font-bold tracking-[-0.015em] text-ink'>{t.contact.booking.title}</h3>
						<p className='mt-2 text-ink-soft'>{BOOKING_URL ? t.contact.booking.textExternal : t.contact.booking.text}</p>
						{slotLabel ? (
							<p className='mt-3 text-ink'>
								<span className='lettering block text-[0.66rem] text-ink-faint'>{t.contact.booking.selected}</span>
								<span className='font-semibold first-letter:uppercase'>{slotLabel}</span>
							</p>
						) : null}
						<a href={BOOKING_URL || '#book'} {...(BOOKING_URL ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className='btn-redline mt-4'>
							{BOOKING_URL ? t.hero.booking.submitExternal : slotLabel ? t.contact.booking.change : t.nav.book}
						</a>
					</div>

					<div className='mt-10 border-t border-ink-line pt-6'>
						<h3 className='type-wide text-xl font-bold tracking-[-0.015em] text-ink'>{t.contact.direct.title}</h3>
						<dl className='mt-3 space-y-3'>
							<div>
								<dt className='text-sm text-ink-faint'>{t.contact.direct.call}</dt>
								<dd>
									<a href={PHONE_HREF} className='type-wide text-xl font-bold text-ink underline decoration-ink-line underline-offset-4 hover:decoration-redline'>
										{PHONE}
									</a>
								</dd>
							</div>
							<div>
								<dt className='text-sm text-ink-faint'>{t.contact.direct.write}</dt>
								<dd>
									<a href={`mailto:${EMAIL}`} className='type-wide break-all text-xl font-bold text-ink underline decoration-ink-line underline-offset-4 hover:decoration-redline'>
										{EMAIL}
									</a>
								</dd>
							</div>
						</dl>
						<div className='mt-3 flex gap-4 pt-1 text-sm'>
							<a href={LINKEDIN_URL} target='_blank' rel='noopener noreferrer' className='text-ink-soft underline decoration-ink-line underline-offset-4 hover:text-ink hover:decoration-redline'>
								LinkedIn
							</a>
							<a href={GITHUB_URL} target='_blank' rel='noopener noreferrer' className='text-ink-soft underline decoration-ink-line underline-offset-4 hover:text-ink hover:decoration-redline'>
								GitHub
							</a>
						</div>
					</div>
				</div>

				<div className='lg:col-span-8'>
					<div className='border border-ink-line bg-sheet-deep/60'>
						<div className='flex items-baseline justify-between gap-4 border-b border-ink-line px-5 py-3 sm:px-8'>
							<h3 className='type-wide text-lg font-bold text-ink'>{b.title}</h3>
							<span className='lettering text-label text-ink-faint'>A-501</span>
						</div>

						{status === 'success' ? (
							<div className='px-5 py-14 sm:px-8' role='status'>
								<svg viewBox='0 0 48 48' className='h-12 w-12' aria-hidden='true'>
									<circle cx='24' cy='24' r='22.5' fill='none' strokeWidth='1.5' className='stroke-redline' />
									<path d='M14 24.5l7 7 13-14' fill='none' strokeWidth='2' className='plot-line stroke-redline' pathLength={1} />
								</svg>
								<p ref={successRef} tabIndex={-1} className='type-wide mt-6 max-w-[34ch] text-2xl font-bold leading-snug text-ink focus:outline-hidden'>
									{b.success}
								</p>
								<p className='mt-2 max-w-[48ch] text-lg leading-relaxed text-ink-soft'>{b.successNext}</p>
								<button type='button' onClick={reset} className='btn-line mt-8'>
									{b.sendAnother}
								</button>
							</div>
						) : (
							<form noValidate onSubmit={submit} className='grid gap-x-8 gap-y-7 px-5 py-8 sm:grid-cols-2 sm:px-8'>
								<p className='text-ink-soft sm:col-span-2'>{b.text}</p>

								<fieldset className='sm:col-span-2'>
									<legend className='lettering text-[0.66rem] text-ink-faint'>{b.topic}</legend>
									<div className='mt-2 grid grid-cols-2 gap-1.5 md:grid-cols-4'>
										{b.topics.map((label, i) => (
											<label key={label} className='group relative cursor-pointer'>
												<input type='radio' name='brief-topic' className='peer sr-only' checked={topic === i} onChange={() => setTopic(i)} />
												<span className='flex h-full items-center justify-center border border-ink-line px-3 py-2.5 text-center text-sm leading-snug text-ink-soft transition-colors group-hover:border-ink-soft group-hover:text-ink peer-checked:border-redline peer-checked:text-redline peer-focus-visible:outline-solid peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-redline'>
													{label}
												</span>
											</label>
										))}
									</div>
								</fieldset>

								<TextField id='brief-name' label={b.name} value={values.name} onChange={update('name')} onBlur={blur('name')} error={show('name')} autoComplete='name' />
								<TextField id='brief-email' label={b.email} type='email' value={values.email} onChange={update('email')} onBlur={blur('email')} error={show('email')} autoComplete='email' />
								<TextField id='brief-phone' label={b.phone} type='tel' value={values.phone} onChange={updatePhone} onBlur={blur('phone')} error={show('phone')} autoComplete='tel' />

								<div>
									<p id='brief-slot-label' className='lettering text-[0.66rem] text-ink-faint'>{b.preferred}</p>
									{/* Not an input: the slot is picked on the timeline above, so the field leads there. */}
									<a
										href={BOOKING_URL || '#book'}
										{...(BOOKING_URL ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
										aria-describedby='brief-slot-label'
										className='field group mt-1 flex items-center justify-between gap-3 hover:border-redline focus-visible:border-redline focus-visible:outline-hidden'>
										<span className={`truncate ${slotLabel ? 'font-semibold first-letter:uppercase' : 'text-ink-faint'}`} title={slotLabel || undefined}>
											{formatSlot(slot, locale, false) || b.preferredNone}
										</span>
										<span className='shrink-0 text-ink-soft transition-colors group-hover:text-redline'>
											<span className='sr-only'>{slotLabel ? b.preferredChange : b.preferredPick}</span>
											<span aria-hidden='true'>↑</span>
										</span>
									</a>
								</div>

								<div className='sm:col-span-2'>
									<label htmlFor='brief-message' className='block lettering text-[0.66rem] text-ink-faint'>
										{b.message}
									</label>
									<textarea
										id='brief-message'
										rows={5}
										value={values.message}
										onChange={update('message')}
										onBlur={blur('message')}
										placeholder={b.messagePlaceholder}
										aria-invalid={!!show('message')}
										aria-describedby={show('message') ? 'brief-message-error' : undefined}
										className={`field mt-1 resize-y ${show('message') ? 'border-redline' : ''}`}
									/>
									{show('message') && (
										<p id='brief-message-error' className='mt-2 text-sm text-redline'>
											{show('message')}
										</p>
									)}
								</div>

								<div className='flex flex-wrap items-center gap-x-6 gap-y-4 sm:col-span-2'>
									<button ref={submitRef} type='submit' disabled={status === 'sending'} aria-busy={status === 'sending'} className='btn-redline w-full disabled:cursor-progress disabled:bg-redline/60 disabled:text-sheet-deep sm:w-auto sm:min-w-48'>
										{status === 'sending' ? b.sending : b.submit}
									</button>
									{attempted && Object.keys(errors).length > 0 && (
										<p role='alert' className='flex items-center gap-2 text-sm text-redline'>
											<svg viewBox='0 0 16 16' className='h-4 w-4 shrink-0' aria-hidden='true'>
												<path d='M8 1.75 15 14.25H1Z' fill='none' stroke='currentColor' strokeWidth='1.25' strokeLinejoin='round' />
												<path d='M8 6.5v3.5M8 11.75v.5' fill='none' stroke='currentColor' strokeWidth='1.5' />
											</svg>
											{b.invalid}
										</p>
									)}
									{status === 'error' && (
										<p role='alert' className='max-w-[48ch] text-sm text-redline'>
											{b.error}{' '}
											<a href={`mailto:${EMAIL}`} className='font-semibold underline underline-offset-4'>
												{EMAIL}
											</a>
											.
										</p>
									)}
								</div>
							</form>
						)}
					</div>
				</div>
			</div>
		</Sheet>
	);
}

interface TextFieldProps {
	id: string;
	label: string;
	value: string;
	type?: string;
	error?: string;
	autoComplete?: string;
	onChange: (e: ChangeEvent<HTMLInputElement>) => void;
	onBlur: () => void;
}

function TextField({ id, label, value, type = 'text', error, autoComplete, onChange, onBlur }: TextFieldProps) {
	return (
		<div>
			<label htmlFor={id} className='block lettering text-[0.66rem] text-ink-faint'>
				{label}
			</label>
			<input id={id} type={type} value={value} onChange={onChange} onBlur={onBlur} autoComplete={autoComplete} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={`field mt-1 ${error ? 'border-redline' : ''}`} />
			{error && (
				<p id={`${id}-error`} className='mt-2 text-sm text-redline'>
					{error}
				</p>
			)}
		</div>
	);
}
